'use client';

import React from 'react';
import Button from '@src/v3/components/coreUI/button';
import { cn } from '@src/lib/utils';

interface ErrorCommonModuleProps {
  /**
   * Icon to display - can be a React component or string (for Icon component)
   */
  icon?: React.ReactNode;
  /**
   * Main heading text
   */
  title?: string;
  /**
   * Description text (optional)
   */
  description?: string;
  /**
   * Button text
   */
  buttonText?: string;
  /**
   * Button click handler
   */
  onButtonClick?: () => void;
  /**
   * Show button or not
   */
  showButton?: boolean;
  /**
   * Custom className for container
   */
  className?: string;
  /**
   * Custom className for icon container
   */
  iconClassName?: string;
  /**
   * Custom className for title
   */
  titleClassName?: string;
  /**
   * Custom className for description
   */
  descriptionClassName?: string;
  /**
   * Button variant
   */
  buttonVariant?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'black'
    | 'destructive'
    | 'outline'
    | 'ghost'
    | 'link'
    | 'background'
    | 'gray';
  /**
   * Button size
   */
  buttonSize?: 'default' | 'sm' | 'lg' | 'icon';
}

export default function ErrorCommonModule({
  icon,
  title = "This page doesn't exist!",
  description,
  buttonText = 'Go Home',
  onButtonClick,
  showButton = true,
  className,
  iconClassName,
  titleClassName,
  descriptionClassName,
  buttonVariant = 'primary',
  buttonSize = 'lg',
}: ErrorCommonModuleProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-8 sm:gap-12 w-full  mx-auto px-5',
        className
      )}
    >
      {/* Icon */}
      {icon && (
        <div
          className={cn(
            'sm:w-[235px] sm:h-[235px] w-[158px] h-[158px] flex items-center justify-center',
            iconClassName
          )}
        >
          {icon}
        </div>
      )}

      {/* Content */}
      <div className="flex flex-col items-center gap-6 w-full">
        {/* Title */}
        {title && (
          <h1
            className={cn(
              'text-2xl sm:text-[32px] font-semibold text-gray-1000 text-center w-full',
              titleClassName
            )}
          >
            {title}
          </h1>
        )}

        {/* Description */}
        {description && (
          <p
            className={cn(
              'text-sm sm:text-base text-gray-700 text-center w-full',
              descriptionClassName
            )}
          >
            {description}
          </p>
        )}
      </div>

      {/* Button */}
      {showButton && (
        <Button
          variant={buttonVariant}
          size={buttonSize}
          onClick={onButtonClick}
          className="rounded-full px-4 h-12 text-base"
        >
          {buttonText}
        </Button>
      )}
    </div>
  );
}
